import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Users, Search, Mail, Phone, Building, CreditCard, Edit2, Download, 
  IndianRupee, Calendar, Clock, FileText, BarChart, Eye, UserCheck,
  GraduationCap, AlertTriangle, CheckCircle, Plus, XCircle, Trash2
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Helper function to get current month in YYYY-MM format
const getCurrentMonth = () => {
  return new Date().toISOString().slice(0, 7);
};

const PayrollManagement = () => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  // Data states
  const [employees, setEmployees] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [leaveStats, setLeaveStats] = useState({});
  const [salarySlips, setSalarySlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Leave Application states
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showLeaveDetailsModal, setShowLeaveDetailsModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [leaveFormData, setLeaveFormData] = useState({
    employee_id: '',
    leave_type: 'Full Day',
    from_date: '',
    to_date: '',
    reason: ''
  });
  const [scheduledClassesForLeave, setScheduledClassesForLeave] = useState([]);
  const [classRemarksForLeave, setClassRemarksForLeave] = useState({});
  const [checkingClassesForLeave, setCheckingClassesForLeave] = useState(false);
  const [classesCheckedForLeave, setClassesCheckedForLeave] = useState(false);

  // Manual Salary Slip Generation states
  const [showManualSalarySlipModal, setShowManualSalarySlipModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [manualSalaryData, setManualSalaryData] = useState({
    month: new Date().toISOString().slice(0, 7), // Current month YYYY-MM
    manual_teaching_hours: 0,
    manual_leaves: 0,
    other_adjustments: 0,
    other_deductions: 0
  });
  
  // Salary Slip Viewer states
  const [showSalarySlipViewer, setShowSalarySlipViewer] = useState(false);
  const [selectedSalarySlip, setSelectedSalarySlip] = useState(null);

  // Report form states
  const [reportForms, setReportForms] = useState({
    payroll: {
      selectedEmployees: 'all',
      selectedMonth: getCurrentMonth(),
      selectedDepartment: 'all'
    },
    teachingHours: {
      selectedEmployee: ''
    },
    employeeReport: {
      reportType: 'all_employees',
      format: 'pdf'
    },
    payrollReport: {
      month: getCurrentMonth(),
      department: 'all'
    },
    teachingHoursReport: {
      employeeId: ''
    }
  });

  // Employee Detail Modal states
  const [showMemberDetailModal, setShowMemberDetailModal] = useState(false);
  const [memberDetailData, setMemberDetailData] = useState(null);

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch both active and inactive employees for leave name resolution
      const token = localStorage.getItem('token');
      const [activeEmpResponse, inactiveEmpResponse] = await Promise.all([
        axios.get(`${API}/payroll/employees`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/payroll/employees?is_active=false`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const activeEmployeesList = activeEmpResponse.data || [];
      const inactiveEmployeesList = inactiveEmpResponse.data || [];
      setEmployees(activeEmployeesList);
      
      // Combine all employees for leave name enrichment
      const allEmployeesList = [...activeEmployeesList, ...inactiveEmployeesList];
      
      // Now fetch everything else — use ALL employees for leave enrichment
      await Promise.all([
        fetchSubjects(),
        fetchLeaves(allEmployeesList),
        fetchLeaveStats(),
        fetchSalarySlips()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalarySlips = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payroll/salary-slips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalarySlips(response.data || []);
    } catch (error) {
      console.error('Error fetching salary slips:', error);
      setSalarySlips([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payroll/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSubjects([]);
    }
  };

  const fetchLeaves = async (employeesList = employees) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payroll/leave-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Enrich leaves with employee details
      const leavesData = response.data || [];
      const enrichedLeaves = leavesData.map(leave => {
        // Try to find employee by ID first
        let employee = employeesList.find(emp => emp.id === leave.employee_id);
        
        // If not found by ID, try by email
        if (!employee && leave.employee_email) {
          employee = employeesList.find(emp => emp.email === leave.employee_email);
        }
        
        // Build display name — add (Inactive) suffix for inactive employees
        let displayName = leave.employee_name || 'Unknown Employee';
        if (employee) {
          displayName = employee.name;
          if (employee.is_active === false) {
            displayName = `${employee.name} (Inactive)`;
          }
        }
        
        return {
          ...leave,
          employee_name: displayName,
          employee_id_display: employee?.employee_id || leave.employee_id || 'N/A',
          employee_email: employee?.email || leave.employee_email || 'N/A'
        };
      });
      
      console.log('Enriched leaves:', enrichedLeaves);
      setLeaves(enrichedLeaves);
    } catch (error) {
      console.error('Error fetching leaves:', error);
      setLeaves([]);
    }
  };

  const fetchLeaveStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const currentMonth = new Date().toISOString().slice(0, 7);
      
      const stats = {};
      for (const employee of employees) {
        try {
          const response = await axios.get(`${API}/payroll/leave-stats/${employee.id}/${currentMonth}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          stats[employee.id] = response.data;
        } catch (error) {
          console.error(`Error fetching leave stats for ${employee.id}:`, error);
          stats[employee.id] = {
            total_leaves: 0,
            paid_leaves: 0,
            unpaid_leaves: 0,
            short_leaves: 0,
            remaining_leaves: 2
          };
        }
      }
      setLeaveStats(stats);
    } catch (error) {
      console.error('Error fetching leave statistics:', error);
      setLeaveStats({});
    }
  };

  // Handle Approve Leave
  const handleApproveLeave = async (leaveId) => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('[APPROVE LEAVE] Attempting to approve:', leaveId);
      console.log('[APPROVE LEAVE] Token exists:', !!token);
      
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }
      
      await axios.put(`${API}/payroll/leave-requests/${leaveId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Leave request approved successfully');
      await fetchLeaves(employees);
    } catch (error) {
      console.error('[APPROVE LEAVE] Error:', error);
      console.error('[APPROVE LEAVE] Error response:', error.response);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please logout and login again.', { duration: 6000 });
      } else if (error.response?.status === 403) {
        toast.error('Permission denied. Contact administrator.', { duration: 6000 });
      } else if (error.response?.status === 503) {
        toast.error('Service temporarily unavailable. Please try again in a moment.', { duration: 6000 });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to approve leave');
      }
    }
  };

  // Handle Reject Leave
  const handleRejectLeave = async (leaveId) => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('[REJECT LEAVE] Attempting to reject:', leaveId);
      console.log('[REJECT LEAVE] Token exists:', !!token);
      
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }
      
      await axios.put(`${API}/payroll/leave-requests/${leaveId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Leave request rejected');
      await fetchLeaves(employees);
    } catch (error) {
      console.error('[REJECT LEAVE] Error:', error);
      console.error('[REJECT LEAVE] Error response:', error.response);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please logout and login again.', { duration: 6000 });
      } else if (error.response?.status === 403) {
        toast.error('Permission denied. Contact administrator.', { duration: 6000 });
      } else if (error.response?.status === 503) {
        toast.error('Service temporarily unavailable. Please try again in a moment.', { duration: 6000 });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to reject leave');
      }
    }
  };

  // Handle Delete Leave Request
  const handleDeleteLeaveRequest = async (leaveId) => {
    if (!window.confirm('Are you sure you want to delete this leave request? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      console.log('[DELETE LEAVE] Attempting to delete:', leaveId);
      console.log('[DELETE LEAVE] Token exists:', !!token);
      
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }
      
      await axios.delete(`${API}/payroll/leave-requests/${leaveId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Leave request deleted successfully');
      await fetchLeaves(employees);
    } catch (error) {
      console.error('[DELETE LEAVE] Error:', error);
      console.error('[DELETE LEAVE] Error response:', error.response);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please logout and login again.', { duration: 6000 });
      } else if (error.response?.status === 403) {
        toast.error('Permission denied. Contact administrator.', { duration: 6000 });
      } else if (error.response?.status === 503) {
        toast.error('Service temporarily unavailable. Please try again in a moment.', { duration: 6000 });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete leave request');
      }
    }
  };

  // Handle Apply for Leave
  const handleApplyLeave = async () => {
    try {
      // Validation
      if (!leaveFormData.from_date || !leaveFormData.to_date) {
        toast.error('Please select from and to dates');
        return;
      }

      // DATE VALIDATION: Ensure To Date is not before From Date
      const fromDate = new Date(leaveFormData.from_date);
      const toDate = new Date(leaveFormData.to_date);
      
      if (toDate < fromDate) {
        toast.error('Invalid date range: "To Date" cannot be before "From Date"');
        return;
      }

      if (!leaveFormData.reason.trim()) {
        toast.error('Please enter reason for leave');
        return;
      }

      // For admin, employee must be selected
      if (isAdmin && !leaveFormData.employee_id) {
        toast.error('Please select an employee');
        return;
      }

      // MANDATORY: Must check for scheduled classes before submitting
      if (!classesCheckedForLeave) {
        toast.error('Please click "Check Scheduled Classes" button to verify if there are any classes during this period');
        return;
      }

      // Check if there are scheduled classes and remarks are required
      if (scheduledClassesForLeave.length > 0) {
        const missingRemarks = scheduledClassesForLeave.filter(session => {
          const remark = classRemarksForLeave[session.id] || '';
          return remark.trim() === '';
        });

        if (missingRemarks.length > 0) {
          toast.error(`Please provide substitute/remarks for all ${scheduledClassesForLeave.length} scheduled classes`);
          return;
        }
      }

      // For non-admin, use current user's email to find employee
      let employeeId = leaveFormData.employee_id;
      if (!isAdmin) {
        console.log('Looking for employee with email:', user?.email);
        console.log('Available employees:', employees.length);
        console.log('Employee emails:', employees.map(e => e.email));
        
        const currentEmployee = employees.find(emp => emp.email === user?.email);
        if (!currentEmployee) {
          console.error('Employee not found. User email:', user?.email, 'Employees:', employees);
          toast.error(`Employee record not found for ${user?.email}. Please contact administrator.`);
          return;
        }
        employeeId = currentEmployee.id;
        console.log('Found employee ID:', employeeId);
      }

      const token = localStorage.getItem('token');
      
      // Calculate days
      const daysDiff = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;

      // Prepare affected classes data
      const affectedClasses = scheduledClassesForLeave.map(session => ({
        session_id: session.id,
        subject_name: session.subject_name || 'Subject',
        batch_name: session.batch_name || 'Batch',
        scheduled_date: session.scheduled_date,
        time_slot: session.time_slot_name || session.time_slot || 'Time',
        classroom_name: session.classroom_name || 'Classroom',
        alternate_arrangement: classRemarksForLeave[session.id] || ''
      }));

      const leaveData = {
        employee_id: employeeId,
        leave_type: leaveFormData.leave_type,
        from_date: leaveFormData.from_date,
        to_date: leaveFormData.to_date,
        reason: leaveFormData.reason,
        status: 'pending',
        days_count: daysDiff,
        affects_salary: false, // Will be calculated by backend
        affected_classes: affectedClasses
      };

      await axios.post(`${API}/payroll/leave-requests`, leaveData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Leave request submitted successfully');
      
      // Reset form and close modal
      setLeaveFormData({
        employee_id: '',
        leave_type: 'Full Day',
        from_date: '',
        to_date: '',
        reason: ''
      });
      setScheduledClassesForLeave([]);
      setClassRemarksForLeave({});
      setClassesCheckedForLeave(false);
      setShowLeaveModal(false);

      // Reload leaves
      fetchLeaves();

    } catch (error) {
      console.error('Error applying for leave:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    }
  };

  // Check scheduled classes for selected employee during leave period
  const checkScheduledClassesForEmployee = async () => {
    if (!leaveFormData.from_date || !leaveFormData.to_date || !leaveFormData.employee_id) {
      toast.error('Please select employee and date range first');
      return;
    }

    try {
      setCheckingClassesForLeave(true);
      setClassesCheckedForLeave(false);
      const token = localStorage.getItem('token');
      
      // Get the selected employee's email
      const selectedEmployee = employees.find(emp => emp.id === leaveFormData.employee_id);
      if (!selectedEmployee) {
        toast.error('Employee not found');
        setScheduledClassesForLeave([]);
        setClassesCheckedForLeave(true);
        setCheckingClassesForLeave(false);
        return;
      }

      const employeeEmail = selectedEmployee.email;

      // Get teacher record for this employee
      const teachersResponse = await axios.get(`${API}/teachers`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const teacherRecord = teachersResponse.data.find(t => 
        t.email && t.email.toLowerCase() === employeeEmail.toLowerCase()
      );

      if (!teacherRecord) {
        console.log('No teacher record found for employee');
        setScheduledClassesForLeave([]);
        setClassesCheckedForLeave(true);
        setCheckingClassesForLeave(false);
        return;
      }

      const teacherId = teacherRecord.id;

      // Fetch sessions for the leave date range and course assignments
      const [sessionsResponse, assignmentsResponse] = await Promise.all([
        axios.get(`${API}/sessions`, { 
          params: {
            date_from: leaveFormData.from_date,
            date_to: leaveFormData.to_date
          },
          headers: { Authorization: `Bearer ${token}` } 
        }),
        axios.get(`${API}/course-assignments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      // Get course assignments for this teacher
      const teacherAssignments = assignmentsResponse.data.filter(a => a.teacher_id === teacherId);
      const assignmentIds = teacherAssignments.map(a => a.id);

      // Filter sessions for this teacher (API already filtered by date range)
      const affectedSessions = sessionsResponse.data.filter(session => {
        const isTeacherSession = 
          (session.teacher_id && session.teacher_id === teacherId) ||
          (session.course_assignment_id && assignmentIds.includes(session.course_assignment_id));
        
        if (!isTeacherSession) {
          return false;
        }
        
        return session.status === 'scheduled';
      });

      setScheduledClassesForLeave(affectedSessions);
      
      // Initialize remarks
      const initialRemarks = {};
      affectedSessions.forEach(session => {
        initialRemarks[session.id] = '';
      });
      setClassRemarksForLeave(initialRemarks);
      setClassesCheckedForLeave(true);

      if (affectedSessions.length > 0) {
        toast.info(`Found ${affectedSessions.length} scheduled class(es) during this period`);
      } else {
        toast.success('No scheduled classes found during this period');
      }

    } catch (err) {
      console.error('Error checking scheduled classes:', err);
      toast.error(`Error checking classes: ${err.response?.data?.detail || err.message}`);
      setClassesCheckedForLeave(false);
      setScheduledClassesForLeave([]);
    } finally {
      setCheckingClassesForLeave(false);
    }
  };

  // Generate manual salary slip with improved logic for different employee types
  // Report generation handlers
  const handleGeneratePayrollReport = async (selectedEmployees, selectedMonth, selectedDepartment) => {
    try {
      toast.info('Generating payroll report...');
      
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (selectedMonth) params.append('month', selectedMonth);
      if (selectedDepartment && selectedDepartment !== 'all') params.append('department', selectedDepartment);
      params.append('format', 'csv'); // Use CSV format for better compatibility
      
      const response = await axios.get(`${API}/payroll/reports/monthly-summary?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Create and download the CSV file
        const csvContent = response.data.content;
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Payroll report generated successfully');
      } else {
        toast.error('Failed to generate payroll report');
      }
    } catch (error) {
      console.error('Error generating payroll report:', error);
      toast.error('Failed to generate payroll report: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleGenerateTeachingHoursReport = async (selectedEmployee) => {
    try {
      toast.info('Generating teaching hours report...');
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payroll/reports/teaching-hours/${selectedEmployee}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Convert data to CSV
        const data = response.data.data;
        if (data.length === 0) {
          toast.info('No teaching hours data found for this employee');
          return;
        }
        
        // Create CSV content
        const headers = Object.keys(data[0]).join(',');
        const csvContent = [headers, ...data.map(row => Object.values(row).join(','))].join('\n');
        
        // Create and download the CSV file
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Teaching hours report generated for ${response.data.employee_name}`);
      } else {
        toast.error('Failed to generate teaching hours report');
      }
    } catch (error) {
      console.error('Error generating teaching hours report:', error);
      toast.error('Failed to generate teaching hours report: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleGenerateEmployeeReport = async (reportType, format) => {
    try {
      toast.info('Generating employee report...');
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payroll/reports/employees?type=${reportType}&format=${format}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const data = response.data.data;
        
        if (data.length === 0) {
          toast.info('No employee data found for the selected criteria');
          return;
        }
        
        // Create CSV content
        const headers = Object.keys(data[0]).join(',');
        const csvContent = [headers, ...data.map(row => 
          Object.values(row).map(value => 
            typeof value === 'string' && value.includes(',') ? `"${value}"` : value
          ).join(',')
        )].join('\n');
        
        // Create and download the file
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Employee report generated successfully (${response.data.total_employees} employees)`);
      } else {
        toast.error('Failed to generate employee report');
      }
    } catch (error) {
      console.error('Error generating employee report:', error);
      toast.error('Failed to generate employee report: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleGenerateManualSalarySlip = async () => {
    if (!selectedMember) return;

    try {
      const token = localStorage.getItem('token');
      
      console.log('[DEBUG] Manual Salary Slip Generation');
      console.log('  Selected Member:', selectedMember.name);
      console.log('  Month from form:', manualSalaryData.month);
      
      // Differentiate logic based on employee type
      const isVisitingFaculty = selectedMember.employment_type?.toLowerCase().includes('visiting') || 
                               selectedMember.visiting_faculty === true;
      
      let payload = {
        employee_id: selectedMember.id,
        month: manualSalaryData.month,
        other_adjustments: parseFloat(manualSalaryData.other_adjustments) || 0,
        other_deductions: parseFloat(manualSalaryData.other_deductions) || 0,
        manual_leaves: parseFloat(manualSalaryData.manual_leaves) || 0,
        manual_teaching_hours: isVisitingFaculty ? (parseFloat(manualSalaryData.manual_teaching_hours) || 0) : 0
      };

      console.log('  Payload:', payload);
      
      // Create salary slip in database (unpublished by default)
      const response = await axios.post(
        `${API}/payroll/salary-slips`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Salary slip created successfully! You can now review and publish it.');
      setShowManualSalarySlipModal(false);
      
      // Reset manual salary data
      setManualSalaryData({
        month: new Date().toISOString().slice(0, 7),
        manual_teaching_hours: 0,
        manual_leaves: 0,
        other_adjustments: 0,
        other_deductions: 0
      });

      // Refresh data to show new salary slip
      fetchData();

    } catch (error) {
      console.error('Error generating manual salary slip:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate manual salary slip');
    }
  };


  // Salary Slip Management Handlers
  const handlePublishSlip = async (slipId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/payroll/salary-slips/${slipId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Salary slip published successfully!');
      fetchSalarySlips(); // Refresh list
    } catch (error) {
      console.error('Error publishing salary slip:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish salary slip');
    }
  };

  const handleUnpublishSlip = async (slipId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/payroll/salary-slips/${slipId}/unpublish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Salary slip unpublished successfully!');
      fetchSalarySlips(); // Refresh list
    } catch (error) {
      console.error('Error unpublishing salary slip:', error);
      toast.error(error.response?.data?.detail || 'Failed to unpublish salary slip');
    }
  };

  const handleDeleteSlip = async (slipId) => {
    if (!window.confirm('Are you sure you want to delete this salary slip? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/payroll/salary-slips/${slipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Salary slip deleted successfully!');
      fetchSalarySlips(); // Refresh list
    } catch (error) {
      console.error('Error deleting salary slip:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete salary slip');
    }
  };

  const handleViewSlip = async (slip) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payroll/salary-slips/${slip.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSalarySlip(response.data);
      setShowSalarySlipViewer(true);
    } catch (error) {
      console.error('Error viewing salary slip:', error);
      toast.error(error.response?.data?.detail || 'Failed to load salary slip');
    }
  };

  const handleDownloadSlipPDF = async (slip) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payroll/salary-slips/${slip.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open in new window for viewing/downloading
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        const htmlContent = response.data.html_content || 'Salary slip content not available';
        newWindow.document.write(htmlContent);
        newWindow.document.close();
      } else {
        toast.error('Please allow pop-ups to view salary slip');
      }
    } catch (error) {
      console.error('Error downloading salary slip PDF:', error);
      toast.error(error.response?.data?.detail || 'Failed to download salary slip');
    }
  };


  // Generate automatic salary slip
  const handleGenerateAutoSalarySlip = async (member) => {
    try {
      const token = localStorage.getItem('token');
      const currentMonth = new Date().toISOString().slice(0, 7);
      
      const response = await axios.get(
        `${API}/payroll/generate-salary-slip?employee_id=${member.id}&month=${currentMonth}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Create a new window/tab to display the salary slip
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        // Backend returns JSON with html_content field
        const htmlContent = response.data.html_content || response.data;
        newWindow.document.write(htmlContent);
        newWindow.document.close();
      } else {
        toast.error('Please allow pop-ups to view salary slip');
      }

      toast.success('Salary slip generated successfully');
    } catch (error) {
      console.error('Error generating salary slip:', error);
      toast.error('Failed to generate salary slip');
    }
  };

  // Get employee's assigned subjects
  const getEmployeeSubjects = (employeeSubjects) => {
    if (!employeeSubjects || employeeSubjects.length === 0) return [];
    return subjects.filter(subject => employeeSubjects.includes(subject.id));
  };

  // Filter employees based on search AND user role
  const filteredEmployees = employees.filter(employee => {
    // First apply search filter
    const matchesSearch = employee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.employee_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // If not admin, only show current user's own record
    if (!isAdmin && !isSuperAdmin) {
      return matchesSearch && employee.email === user?.email;
    }
    
    // Admins can see all employees
    return matchesSearch;
  });

  // Check if employee is visiting faculty
  const isVisitingFaculty = (member) => {
    return member.employment_type?.toLowerCase().includes('visiting') || 
           member.visiting_faculty === true;
  };

  // Overview Tab Content
  const renderOverview = () => {
    // Non-admin users should not see overview statistics
    if (!isAdmin && !isSuperAdmin) {
      return (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-gray-600">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-orange-500" />
              <p className="text-lg font-medium">Access Restricted</p>
              <p className="text-sm mt-2">This section is only available to administrators.</p>
              <p className="text-sm mt-1">Please use the "Payroll" tab to view your personal information.</p>
            </div>
          </CardContent>
        </Card>
      );
    }
    
    return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Employees</CardTitle>
          <Users className="h-4 w-4 text-blue-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-600">{employees.length}</div>
          <p className="text-xs text-gray-600">Active employees</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Teaching Staff</CardTitle>
          <GraduationCap className="h-4 w-4 text-green-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">
            {employees.filter(m => m.role === 'teaching_staff').length}
          </div>
          <p className="text-xs text-gray-600">Faculty members</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Administration</CardTitle>
          <Building className="h-4 w-4 text-purple-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-purple-600">
            {employees.filter(m => m.role === 'admin').length}
          </div>
          <p className="text-xs text-gray-600">Admin staff</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Pending Leaves</CardTitle>
          <AlertTriangle className="h-4 w-4 text-orange-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-orange-600">
            {leaves.filter(l => l.status === 'pending').length}
          </div>
          <p className="text-xs text-gray-600">Awaiting approval</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">This Month Leaves</CardTitle>
          <Calendar className="h-4 w-4 text-red-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-red-600">
            {Object.values(leaveStats).reduce((sum, stat) => sum + (stat.total_leaves || 0), 0)}
          </div>
          <p className="text-xs text-gray-600">Days taken</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Available Subjects</CardTitle>
          <FileText className="h-4 w-4 text-indigo-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-indigo-600">{subjects.length}</div>
          <p className="text-xs text-gray-600">Course subjects</p>
        </CardContent>
      </Card>
    </div>
    );
  };

  // Members/Employees Tab Content - Shows employee cards with Manual buttons
  const renderMembers = () => (
    <div className="space-y-6">
      {/* Info message for non-admin users */}
      {!isAdmin && !isSuperAdmin && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Viewing Your Personal Payroll Information</p>
                <p className="text-sm text-blue-700 mt-1">
                  You can only view and manage your own salary slips and payroll data. 
                  Contact an administrator for any assistance.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Employees Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredEmployees.map(employee => (
          <Card key={employee.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-lg">
                    {employee.name?.charAt(0)?.toUpperCase() || 'E'}
                  </span>
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">{employee.name}</CardTitle>
                  <div className="flex items-center mt-1 space-x-2">
                    <Badge variant="outline" className="text-xs">
                      {employee.role === 'teaching_staff' ? 'Teaching Staff' : 'Administration'}
                    </Badge>
                    {isVisitingFaculty(employee) && (
                      <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800">
                        Visiting Faculty
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-700">{employee.phone}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Building className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">
                    {Array.isArray(employee.departments) ? employee.departments.join(', ') : (employee.department || 'No department')}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <CreditCard className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{employee.employee_id || 'No ID'}</span>
                </div>
                
                {/* Show assigned subjects for teaching staff */}
                {employee.role === 'teaching_staff' && (
                  <div className="mt-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <GraduationCap className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-700 font-medium">Assigned Subjects:</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {getEmployeeSubjects(employee.subjects).map(subject => (
                        <Badge key={subject.id} variant="secondary" className="text-xs">
                          {subject.name}
                        </Badge>
                      ))}
                      {(!employee.subjects || employee.subjects.length === 0) && (
                        <span className="text-xs text-gray-500">No subjects assigned</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Show current leave stats */}
                {leaveStats[employee.id] && (
                  <div className="mt-3 p-2 bg-gray-50 rounded">
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">This month: </span>
                      {leaveStats[employee.id].total_leaves || 0} days taken, 
                      {leaveStats[employee.id].remaining_leaves || 0} remaining
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex space-x-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setMemberDetailData(employee);
                    setShowMemberDetailModal(true);
                  }}
                  className="flex-1"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  View
                </Button>
                {/* Manual salary slip generation - ADMIN ONLY */}
                {(isAdmin || isSuperAdmin) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      setSelectedMember(employee);
                      const currentMonth = new Date().toISOString().slice(0, 7);
                      setManualSalaryData(prev => ({
                        ...prev,
                        month: currentMonth,
                        manual_teaching_hours: 0,
                        manual_leaves: 0,
                        other_adjustments: 0,
                        other_deductions: 0
                      }));
                      
                      // Auto-calculate teaching hours for visiting faculty
                      const isVisitingFaculty = employee.employment_type?.toLowerCase().includes('visiting');
                      if (isVisitingFaculty) {
                        try {
                          const token = localStorage.getItem('token');
                          const response = await axios.get(
                            `${API}/payroll/calculate-teaching-hours/${employee.id}/${currentMonth}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          
                          if (response.data.success) {
                            setManualSalaryData(prev => ({
                              ...prev,
                              manual_teaching_hours: response.data.total_hours,
                              month: currentMonth
                            }));
                            toast.success(`Auto-calculated ${response.data.total_hours} hours from ${response.data.total_sessions} sessions`);
                          }
                        } catch (error) {
                          console.error('Error calculating teaching hours:', error);
                          toast.info('Could not auto-calculate hours. Please enter manually.');
                        }
                      }
                      
                      setShowManualSalarySlipModal(true);
                    }}
                    className="flex-1"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Generate Slip
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredEmployees.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No employees found</h3>
            <p className="text-gray-600">
              {searchTerm ? 'Try adjusting your search criteria' : 'No employees available'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Leaves Tab Content
  const renderLeaves = () => (
    <div className="space-y-6">
      {/* Apply for Leave Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Leave Management</h2>
        <Button 
          onClick={() => setShowLeaveModal(true)}
          className="flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Apply for Leave</span>
        </Button>
      </div>

      {/* Leave Requests grouped by month */}
      {leaves.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No leave requests found</p>
              <p className="text-sm text-gray-500 mt-2">Click "Apply for Leave" to create your first request</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {(() => {
            // Group leaves by month based on from_date
            const leavesByMonth = leaves.reduce((acc, leave) => {
              const leaveDate = new Date(leave.from_date);
              const monthYear = `${leaveDate.getFullYear()}-${String(leaveDate.getMonth() + 1).padStart(2, '0')}`;
              const monthName = leaveDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
              
              if (!acc[monthYear]) {
                acc[monthYear] = {
                  monthName,
                  leaves: [],
                  sortDate: leaveDate
                };
              }
              acc[monthYear].leaves.push(leave);
              return acc;
            }, {});
            
            // Sort months in descending order (most recent first)
            const sortedMonths = Object.entries(leavesByMonth).sort((a, b) => 
              b[1].sortDate - a[1].sortDate
            );
            
            return sortedMonths.map(([monthKey, monthData]) => (
              <Card key={monthKey} className="border-l-4 border-l-blue-500">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center text-lg">
                      <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                      {monthData.monthName}
                    </CardTitle>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                      {monthData.leaves.length} {monthData.leaves.length === 1 ? 'Leave' : 'Leaves'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {monthData.leaves
                      .sort((a, b) => new Date(b.from_date) - new Date(a.from_date))
                      .map(leave => (
                        <div key={leave.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 bg-white">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-semibold text-sm">
                                {leave.employee_name?.charAt(0)?.toUpperCase() || 'U'}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">{leave.employee_name}</p>
                                <p className="text-xs text-gray-500">{leave.employee_id_display} • {leave.employee_email}</p>
                              </div>
                              <Badge variant={leave.status === 'approved' ? 'default' : leave.status === 'pending' ? 'secondary' : 'destructive'}>
                                {leave.status}
                              </Badge>
                            </div>
                            <div className="ml-10">
                              <p className="text-sm text-gray-700 font-medium">
                                {leave.leave_type} • {leave.from_date} to {leave.to_date}
                              </p>
                              {leave.reason && (
                                <p className="text-xs text-gray-600 mt-1 bg-gray-50 p-2 rounded">
                                  <span className="font-medium">Reason:</span> {leave.reason}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-right">
                              <p className="text-sm font-medium">{leave.total_days || leave.days_count} days</p>
                            </div>
                            {(isAdmin || isSuperAdmin) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                                onClick={() => {
                                  setSelectedLeave(leave);
                                  setShowLeaveDetailsModal(true);
                                }}
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                View Details
                              </Button>
                            )}
                            {(isAdmin || isSuperAdmin) && leave.status === 'pending' && (
                              <div className="flex space-x-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                                  onClick={() => handleApproveLeave(leave.id)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                                  onClick={() => handleRejectLeave(leave.id)}
                                >
                                  <AlertTriangle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}
                            {(isAdmin || isSuperAdmin) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                                onClick={() => handleDeleteLeaveRequest(leave.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            ));
          })()}
        </div>
      )}

      {/* Apply for Leave Modal */}
      <Dialog open={showLeaveModal} onOpenChange={setShowLeaveModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apply for Leave</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Employee Selection (for admin only - NEVER show to non-admin users) */}
            {(isAdmin || isSuperAdmin) && employees.length > 1 && (
              <div>
                <Label>Employee</Label>
                <Select 
                  value={leaveFormData.employee_id}
                  onValueChange={(value) => setLeaveFormData(prev => ({ ...prev, employee_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} - {emp.employee_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Leave Type */}
            <div>
              <Label>Leave Type</Label>
              <Select 
                value={leaveFormData.leave_type}
                onValueChange={(value) => setLeaveFormData(prev => ({ ...prev, leave_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Full Day">Full Day</SelectItem>
                  <SelectItem value="Half Day">Half Day</SelectItem>
                  <SelectItem value="Short Leave">Short Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* From Date */}
            <div>
              <Label>From Date</Label>
              <Input 
                type="date"
                value={leaveFormData.from_date}
                onChange={(e) => setLeaveFormData(prev => ({ ...prev, from_date: e.target.value }))}
              />
            </div>

            {/* To Date */}
            <div>
              <Label>To Date</Label>
              <Input 
                type="date"
                value={leaveFormData.to_date}
                onChange={(e) => setLeaveFormData(prev => ({ ...prev, to_date: e.target.value }))}
              />
            </div>

            {/* Reason */}
            <div>
              <Label>Reason</Label>
              <Textarea 
                placeholder="Enter reason for leave"
                value={leaveFormData.reason}
                onChange={(e) => setLeaveFormData(prev => ({ ...prev, reason: e.target.value }))}
                rows={3}
              />
            </div>

            {/* Check Scheduled Classes Button */}
            {leaveFormData.from_date && leaveFormData.to_date && leaveFormData.employee_id && (
              <div>
                <Button 
                  type="button"
                  onClick={checkScheduledClassesForEmployee}
                  disabled={checkingClassesForLeave}
                  variant="outline"
                  className="w-full"
                >
                  {checkingClassesForLeave ? 'Checking...' : 'Check Scheduled Classes'}
                </Button>
                {classesCheckedForLeave && (
                  <p className="text-sm text-green-600 mt-2">
                    ✓ Classes checked - {scheduledClassesForLeave.length} scheduled class(es) found
                  </p>
                )}
              </div>
            )}

            {/* Show Affected Classes */}
            {classesCheckedForLeave && scheduledClassesForLeave.length > 0 && (
              <div className="border-t pt-4">
                <Label className="text-red-600">⚠️ Scheduled Classes During Leave ({scheduledClassesForLeave.length})</Label>
                <p className="text-sm text-gray-600 mb-3">
                  Please provide substitute arrangement or remarks for each class:
                </p>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {scheduledClassesForLeave.map(session => (
                    <div key={session.id} className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="font-medium text-sm">
                        {session.subject_name || 'Subject'} - {session.batch_name || 'Batch'}
                      </p>
                      <p className="text-xs text-gray-600">
                        {session.scheduled_date} | {session.time_slot_name || session.time_slot} | {session.classroom_name || 'Classroom'}
                      </p>
                      <Textarea
                        placeholder="Enter substitute arrangement or remarks"
                        value={classRemarksForLeave[session.id] || ''}
                        onChange={(e) => setClassRemarksForLeave(prev => ({
                          ...prev,
                          [session.id]: e.target.value
                        }))}
                        rows={2}
                        className="mt-2 text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleApplyLeave}>
              Submit Leave Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Generate Documents Tab Content (merged with Reports)
  const renderGenerateDocuments = () => {
    // Non-admin users should not generate reports
    if (!isAdmin && !isSuperAdmin) {
      return (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-gray-600">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-orange-500" />
              <p className="text-lg font-medium">Access Restricted</p>
              <p className="text-sm mt-2">Report generation is only available to administrators.</p>
            </div>
          </CardContent>
        </Card>
      );
    }
    
    return (
    <div className="space-y-6">
      {/* Document Generation Section */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Employee Master Reports</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Generate comprehensive employee master data reports.
            </p>
            <div className="space-y-3">
              <div>
                <Label>Report Type</Label>
                <Select
                  value={reportForms.employeeReport.reportType}
                  onValueChange={(value) => setReportForms(prev => ({
                    ...prev,
                    employeeReport: { ...prev.employeeReport, reportType: value }
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select report type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_employees">All Employees</SelectItem>
                    <SelectItem value="teaching_staff">Teaching Staff Only</SelectItem>
                    <SelectItem value="admin_staff">Administration Only</SelectItem>
                    <SelectItem value="by_department">Department Wise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Format</Label>
                <Select
                  value={reportForms.employeeReport.format}
                  onValueChange={(value) => setReportForms(prev => ({
                    ...prev,
                    employeeReport: { ...prev.employeeReport, format: value }
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF Report</SelectItem>
                    <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                    <SelectItem value="csv">CSV File</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                className="w-full"
                onClick={() => handleGenerateEmployeeReport(reportForms.employeeReport.reportType, reportForms.employeeReport.format)}
              >
                <FileText className="w-4 h-4 mr-2" />
                Generate Report
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Bulk Salary Slips</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Generate salary slips for multiple employees at once.
            </p>
            <div className="space-y-3">
              <div>
                <Label>Select Month</Label>
                <Select defaultValue={new Date().toISOString().slice(0, 7)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = String(i + 1).padStart(2, '0');
                      const value = `2025-${month}`;
                      const date = new Date(2025, i, 1);
                      return (
                        <SelectItem key={value} value={value}>
                          {date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Employee Selection</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    <SelectItem value="teaching_staff">Teaching Staff Only</SelectItem>
                    <SelectItem value="admin_staff">Administration Only</SelectItem>
                    <SelectItem value="custom">Custom Selection</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full">
                <Download className="w-4 h-4 mr-2" />
                Generate Bulk Slips
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reports Section (formerly separate tab) */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Monthly Payroll Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Generate comprehensive monthly payroll reports.
            </p>
            <div className="space-y-3">
              <div>
                <Label>Select Month</Label>
                <Select 
                  value={reportForms.payrollReport.month}
                  onValueChange={(value) => setReportForms(prev => ({
                    ...prev,
                    payrollReport: { ...prev.payrollReport, month: value }
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = String(i + 1).padStart(2, '0');
                      const value = `2025-${month}`;
                      const date = new Date(2025, i, 1);
                      return (
                        <SelectItem key={value} value={value}>
                          {date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Department</Label>
                <Select
                  value={reportForms.payrollReport.department}
                  onValueChange={(value) => setReportForms(prev => ({
                    ...prev,
                    payrollReport: { ...prev.payrollReport, department: value }
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    <SelectItem value="Fashion Design">Fashion Design</SelectItem>
                    <SelectItem value="Interior Design">Interior Design</SelectItem>
                    <SelectItem value="MBA Design Management">MBA Design Management</SelectItem>
                    <SelectItem value="Administration">Administration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                className="w-full"
                onClick={() => handleGeneratePayrollReport(
                  [], // selectedEmployees - not used in current implementation
                  reportForms.payrollReport.month,
                  reportForms.payrollReport.department
                )}
              >
                <BarChart className="w-4 h-4 mr-2" />
                Generate Payroll Report
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Teaching Hours Report</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Generate teaching hours report for faculty members.
            </p>
            <div className="space-y-3">
              <div>
                <Label>Select Faculty</Label>
                <Select
                  value={reportForms.teachingHoursReport.employeeId}
                  onValueChange={(value) => setReportForms(prev => ({
                    ...prev,
                    teachingHoursReport: { ...prev.teachingHoursReport, employeeId: value }
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose faculty" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.filter(employee => employee.role === 'teaching_staff').map(employee => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.name} - {Array.isArray(employee.departments) ? employee.departments.join(', ') : (employee.department || '')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Month (Optional)</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="All months" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = String(i + 1).padStart(2, '0');
                      const value = `2025-${month}`;
                      const date = new Date(2025, i, 1);
                      return (
                        <SelectItem key={value} value={value}>
                          {date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                className="w-full"
                onClick={() => handleGenerateTeachingHoursReport(reportForms.teachingHoursReport.employeeId)}
                disabled={!reportForms.teachingHoursReport.employeeId}
              >
                <Clock className="w-4 h-4 mr-2" />
                Generate Hours Report
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    );
  };

  // Main render content based on active tab


  // Salary Slips Tab Content
  const renderSalarySlips = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">Salary Slips Management</h2>
          <p className="text-sm text-gray-600">
            {isAdmin || isSuperAdmin 
              ? 'Create, review, and publish salary slips' 
              : 'View and download your published salary slips'}
          </p>
        </div>
      </div>

      {/* Salary Slips List */}
      <div className="space-y-4">
        {salarySlips.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">
                {isAdmin || isSuperAdmin 
                  ? 'No salary slips created yet. Use "Generate Slip" button in Payroll tab to create salary slips.' 
                  : 'No published salary slips available yet.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          salarySlips.map(slip => (
            <Card key={slip.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold">{slip.employee_name}</h3>
                      {slip.is_published ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Published
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Draft
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Month:</span> {new Date(slip.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </div>
                      <div>
                        <span className="font-medium">Net Salary:</span> ₹{slip.net_salary?.toLocaleString()}
                      </div>
                      <div>
                        <span className="font-medium">Generated:</span> {new Date(slip.generated_at).toLocaleDateString()}
                      </div>
                      {slip.is_published && slip.published_at && (
                        <div>
                          <span className="font-medium">Published:</span> {new Date(slip.published_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2">
                    {/* View Button - All users can view published slips, admins can view all */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewSlip(slip)}
                      className="flex items-center"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>

                    {/* Download PDF Button */}
                    {(slip.is_published || isAdmin || isSuperAdmin) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadSlipPDF(slip)}
                        className="flex items-center"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    )}

                    {/* Admin-only buttons */}
                    {(isAdmin || isSuperAdmin) && (
                      <>
                        {!slip.is_published ? (
                          <Button
                            size="sm"
                            onClick={() => handlePublishSlip(slip.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Publish
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnpublishSlip(slip.id)}
                            className="border-orange-300 text-orange-700 hover:bg-orange-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Unpublish
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteSlip(slip.id)}
                          className="border-red-300 text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'leaves':
        return renderLeaves();
      case 'payroll':
        return renderMembers(); // Show employee cards with all salary buttons
      case 'salary-slips':
        return renderSalarySlips(); // New salary slips management
      case 'documents':
        return renderGenerateDocuments();
      default:
        return renderOverview();
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading payroll data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Smart Payroll</h1>
          <p className="text-gray-600">Comprehensive payroll and employee management</p>
        </div>
        {employees.length > 0 && (
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Employees</p>
            <p className="text-2xl font-bold text-blue-600">{employees.length}</p>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Member Detail Modal */}
      {showMemberDetailModal && memberDetailData && (
        <Dialog open={showMemberDetailModal} onOpenChange={setShowMemberDetailModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Employee Details - {memberDetailData.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Email:</strong> {memberDetailData.email}
                </div>
                <div>
                  <strong>Phone:</strong> {memberDetailData.phone || 'Not provided'}
                </div>
                <div>
                  <strong>Employee ID:</strong> {memberDetailData.employee_id || 'Not assigned'}
                </div>
                <div>
                  <strong>Role:</strong> {memberDetailData.role === 'teaching_staff' ? 'Teaching Staff' : 'Administration'}
                </div>
                <div>
                  <strong>Department:</strong> {Array.isArray(memberDetailData.departments) ? memberDetailData.departments.join(', ') : (memberDetailData.department || 'Not specified')}
                </div>
                <div>
                  <strong>Designation:</strong> {memberDetailData.designation || 'Not specified'}
                </div>
                {memberDetailData.date_of_joining && (
                  <div>
                    <strong>Date of Joining:</strong> {new Date(memberDetailData.date_of_joining).toLocaleDateString()}
                  </div>
                )}
                <div>
                  <strong>Employment Type:</strong> {memberDetailData.employment_type || 'Full-time'}
                </div>
                <div>
                  <strong>Base Salary:</strong> ₹{memberDetailData.base_salary || 0}
                </div>
                <div>
                  <strong>Visiting Faculty:</strong> {isVisitingFaculty(memberDetailData) ? 'Yes' : 'No'}
                </div>
              </div>
              
              {memberDetailData.role === 'teaching_staff' && memberDetailData.subjects && (
                <div>
                  <strong className="block mb-2">Assigned Subjects:</strong>
                  <div className="flex flex-wrap gap-2">
                    {getEmployeeSubjects(memberDetailData.subjects).map(subject => (
                      <Badge key={subject.id} variant="secondary">
                        {subject.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Manual Salary Slip Generation Modal - ENHANCED LOGIC */}
      {showManualSalarySlipModal && selectedMember && (
        <Dialog open={showManualSalarySlipModal} onOpenChange={setShowManualSalarySlipModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Generate Manual Salary Slip</DialogTitle>
              <DialogDescription>
                Enter manual values for {selectedMember?.name}'s salary slip for {manualSalaryData.month}
                {isVisitingFaculty(selectedMember) && (
                  <Badge variant="secondary" className="ml-2 bg-orange-100 text-orange-800">
                    Visiting Faculty - Hourly Calculation
                  </Badge>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <Label htmlFor="salary_month">Salary Month</Label>
                <Select 
                  value={manualSalaryData.month}
                  onValueChange={async (value) => {
                    setManualSalaryData(prev => ({ ...prev, month: value }));
                    
                    // Auto-recalculate teaching hours for visiting faculty when month changes
                    const isVisitingFaculty = selectedMember.employment_type?.toLowerCase().includes('visiting');
                    if (isVisitingFaculty) {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.get(
                          `${API}/payroll/calculate-teaching-hours/${selectedMember.id}/${value}`,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        
                        if (response.data.success) {
                          setManualSalaryData(prev => ({
                            ...prev,
                            manual_teaching_hours: response.data.total_hours,
                            month: value
                          }));
                          toast.success(`Auto-calculated ${response.data.total_hours} hours from ${response.data.total_sessions} sessions`);
                        }
                      } catch (error) {
                        console.error('Error calculating teaching hours:', error);
                        // Don't show error toast as user can still enter manually
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = String(i + 1).padStart(2, '0');
                      const value = `2025-${month}`;
                      const date = new Date(2025, i, 1);
                      return (
                        <SelectItem key={value} value={value}>
                          {date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Differentiated Logic for Visiting Faculty vs Regular Employees */}
              {isVisitingFaculty(selectedMember) ? (
                // VISITING FACULTY - Hourly Calculation
                <>
                  <div className="border-l-4 border-orange-400 pl-4 bg-orange-50 p-4 rounded">
                    <h3 className="font-semibold text-orange-800 mb-3">Visiting Faculty - Hourly Calculation</h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="manual_teaching_hours" className="flex items-center gap-2">
                          Teaching Hours *
                          <Badge variant="outline" className="text-xs">Auto-calculated from sessions</Badge>
                        </Label>
                        <Input
                          id="manual_teaching_hours"
                          type="number"
                          min="0"
                          step="0.5"
                          value={manualSalaryData.manual_teaching_hours}
                          onChange={(e) => setManualSalaryData(prev => ({
                            ...prev,
                            manual_teaching_hours: parseFloat(e.target.value) || 0
                          }))}
                          placeholder="0.0"
                          className="text-right"
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          Hours are auto-calculated from scheduled sessions. You can edit if needed.
                        </p>
                        {selectedMember.hourly_rate && (
                          <p className="text-xs text-green-700 mt-1 font-medium">
                            Rate: ₹{selectedMember.hourly_rate}/hour | 
                            Estimated: ₹{(manualSalaryData.manual_teaching_hours * selectedMember.hourly_rate).toFixed(2)}
                          </p>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded border border-blue-200">
                        <strong>Note:</strong> Leave tracking is not applicable for visiting faculty. 
                        Salary is calculated based on actual hours taught.
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                // REGULAR EMPLOYEES - Simple Leave Adjustment
                <>
                  <div className="border-l-4 border-blue-400 pl-4 bg-blue-50 p-4 rounded">
                    <h3 className="font-semibold text-blue-800 mb-3">Regular Employee - Leave Adjustment</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label htmlFor="manual_leaves">Additional Leave Days (if any)</Label>
                        <Input
                          id="manual_leaves"
                          type="number"
                          step="0.5"
                          min="0"
                          value={manualSalaryData.manual_leaves}
                          onChange={(e) => setManualSalaryData(prev => ({
                            ...prev,
                            manual_leaves: parseFloat(e.target.value) || 0
                          }))}
                          placeholder="0"
                          className="text-right"
                        />
                        <p className="text-sm text-gray-600 mt-1">
                          Leave deduction will be calculated automatically based on your leave rules. Supports half days (e.g., 2.5)
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Common Adjustments and Deductions */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-800 mb-3">Adjustments & Deductions</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="other_adjustments">Other Adjustments (₹)</Label>
                    <Input
                      id="other_adjustments"
                      type="number"
                      step="0.01"
                      value={manualSalaryData.other_adjustments}
                      onChange={(e) => setManualSalaryData(prev => ({
                        ...prev,
                        other_adjustments: parseFloat(e.target.value) || 0
                      }))}
                      placeholder="0.00"
                      className="text-right"
                    />
                    <p className="text-xs text-gray-600 mt-1">Additional payments (+)</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="other_deductions">Other Deductions (₹)</Label>
                    <Input
                      id="other_deductions"
                      type="number"
                      step="0.01"
                      value={manualSalaryData.other_deductions}
                      onChange={(e) => setManualSalaryData(prev => ({
                        ...prev,
                        other_deductions: parseFloat(e.target.value) || 0
                      }))}
                      placeholder="0.00"
                      className="text-right"
                    />
                    <p className="text-xs text-gray-600 mt-1">Additional deductions (-)</p>
                  </div>
                </div>
              </div>

              {/* Calculation Preview */}
              <div className="bg-gray-50 p-4 rounded">
                <h4 className="font-semibold text-gray-800 mb-2">Calculation Preview</h4>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span>Base Salary:</span>
                    <span>₹{selectedMember.base_salary || 0}</span>
                  </div>
                  {isVisitingFaculty(selectedMember) ? (
                    <div className="flex justify-between text-orange-600">
                      <span>Teaching Hours ({manualSalaryData.manual_teaching_hours}h):</span>
                      <span>Hourly Rate × Hours</span>
                    </div>
                  ) : (
                    <>
                      {/* Allowances removed as per user requirement */}
                    </>
                  )}
                  <div className="flex justify-between text-blue-600">
                    <span>Other Adjustments:</span>
                    <span>+₹{manualSalaryData.other_adjustments || 0}</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>Leave Deductions:</span>
                    <span>Based on {manualSalaryData.manual_leaves?.toFixed(1) || '0.0'} days</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>Other Deductions:</span>
                    <span>-₹{manualSalaryData.other_deductions || 0}</span>
                  </div>
                  <div className="border-t pt-1 mt-2">
                    <div className="flex justify-between font-semibold">
                      <span>Net Salary:</span>
                      <span className="text-green-600">Calculated on generation</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowManualSalarySlipModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleGenerateManualSalarySlip}>
                <Download className="w-4 h-4 mr-2" />
                Generate Salary Slip
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Salary Slip Viewer Modal */}
      {showSalarySlipViewer && selectedSalarySlip && (
        <Dialog open={showSalarySlipViewer} onOpenChange={setShowSalarySlipViewer}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Salary Slip - {selectedSalarySlip.employee_name}
                {selectedSalarySlip.is_published ? (
                  <Badge className="ml-3 bg-green-100 text-green-800 border-green-200">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Published
                  </Badge>
                ) : (
                  <Badge variant="outline" className="ml-3 bg-yellow-50 text-yellow-800 border-yellow-200">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Draft
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                {new Date(selectedSalarySlip.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Salary Summary */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-semibold text-lg mb-4">Salary Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Base Salary</p>
                    <p className="text-lg font-semibold">₹{selectedSalarySlip.base_salary?.toLocaleString()}</p>
                  </div>
                  {selectedSalarySlip.teaching_hours_amount > 0 && (
                    <div>
                      <p className="text-gray-600">Teaching Hours ({selectedSalarySlip.teaching_hours}h)</p>
                      <p className="text-lg font-semibold">₹{selectedSalarySlip.teaching_hours_amount?.toLocaleString()}</p>
                    </div>
                  )}
                  {selectedSalarySlip.other_adjustments > 0 && (
                    <div>
                      <p className="text-gray-600">Other Adjustments</p>
                      <p className="text-lg font-semibold text-green-600">+₹{selectedSalarySlip.other_adjustments?.toLocaleString()}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-600">Gross Salary</p>
                    <p className="text-lg font-semibold text-blue-600">₹{selectedSalarySlip.gross_salary?.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div className="bg-red-50 p-6 rounded-lg">
                <h3 className="font-semibold text-lg mb-4">Deductions</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedSalarySlip.leave_deduction > 0 && (
                    <div>
                      <p className="text-gray-600">Leave Deduction ({selectedSalarySlip.manual_leaves?.toFixed(1)} days)</p>
                      <p className="text-lg font-semibold text-red-600">-₹{selectedSalarySlip.leave_deduction?.toLocaleString()}</p>
                    </div>
                  )}
                  {selectedSalarySlip.professional_tax > 0 && (
                    <div>
                      <p className="text-gray-600">Professional Tax</p>
                      <p className="text-lg font-semibold text-red-600">-₹{selectedSalarySlip.professional_tax?.toLocaleString()}</p>
                    </div>
                  )}
                  {selectedSalarySlip.other_deductions > 0 && (
                    <div>
                      <p className="text-gray-600">Other Deductions</p>
                      <p className="text-lg font-semibold text-red-600">-₹{selectedSalarySlip.other_deductions?.toLocaleString()}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-600">Total Deductions</p>
                    <p className="text-lg font-semibold text-red-600">-₹{selectedSalarySlip.total_deductions?.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Net Salary */}
              <div className="bg-green-50 p-6 rounded-lg border-2 border-green-200">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-gray-600 text-sm">Net Salary</p>
                    <p className="text-3xl font-bold text-green-700">₹{selectedSalarySlip.net_salary?.toLocaleString()}</p>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <p>Generated by: {selectedSalarySlip.generated_by}</p>
                    <p>Generated on: {new Date(selectedSalarySlip.generated_at).toLocaleDateString()}</p>
                    {selectedSalarySlip.is_published && selectedSalarySlip.published_by && (
                      <>
                        <p>Published by: {selectedSalarySlip.published_by}</p>
                        <p>Published on: {new Date(selectedSalarySlip.published_at).toLocaleDateString()}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setShowSalarySlipViewer(false)}>
                  Close
                </Button>
                <Button onClick={() => handleDownloadSlipPDF(selectedSalarySlip)}>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Leave Details Modal */}
      {showLeaveDetailsModal && selectedLeave && (
        <Dialog open={showLeaveDetailsModal} onOpenChange={setShowLeaveDetailsModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Leave Request Details</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Employee Information */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Employee Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Name:</p>
                    <p className="font-semibold text-gray-900">{selectedLeave.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Employee ID:</p>
                    <p className="font-semibold text-gray-900">{selectedLeave.employee_id_display}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Email:</p>
                    <p className="font-semibold text-gray-900">{selectedLeave.employee_email}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Status:</p>
                    <Badge variant={selectedLeave.status === 'approved' ? 'default' : selectedLeave.status === 'pending' ? 'secondary' : 'destructive'}>
                      {selectedLeave.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Leave Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Leave Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Leave Type:</p>
                    <p className="font-semibold text-gray-900">{selectedLeave.leave_type}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Duration:</p>
                    <p className="font-semibold text-gray-900">{selectedLeave.total_days || selectedLeave.days_count} days</p>
                  </div>
                  <div>
                    <p className="text-gray-600">From Date:</p>
                    <p className="font-semibold text-gray-900">{selectedLeave.from_date}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">To Date:</p>
                    <p className="font-semibold text-gray-900">{selectedLeave.to_date}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-600 mb-1">Reason:</p>
                    <p className="bg-white p-3 rounded border border-gray-200">{selectedLeave.reason}</p>
                  </div>
                </div>
              </div>

              {/* Affected Classes */}
              {selectedLeave.affected_classes && selectedLeave.affected_classes.length > 0 && (
                <div className="bg-yellow-50 border-2 border-yellow-300 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-3 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Affected Classes ({selectedLeave.affected_classes.length})
                  </h3>
                  <div className="space-y-3">
                    {selectedLeave.affected_classes.map((affectedClass, index) => (
                      <div key={index} className="bg-white p-4 rounded-lg border border-yellow-200">
                        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                          <div>
                            <p className="text-gray-600">Subject:</p>
                            <p className="font-semibold text-gray-900">{affectedClass.subject_name}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Batch:</p>
                            <p className="font-semibold text-gray-900">{affectedClass.batch_name}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Date:</p>
                            <p className="font-semibold text-gray-900">{new Date(affectedClass.scheduled_date).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Time:</p>
                            <p className="font-semibold text-gray-900">{affectedClass.time_slot}</p>
                          </div>
                          {affectedClass.classroom_name && (
                            <div>
                              <p className="text-gray-600">Classroom:</p>
                              <p className="font-semibold text-gray-900">{affectedClass.classroom_name}</p>
                            </div>
                          )}
                        </div>
                        <div className="pt-3 border-t border-gray-200">
                          <p className="text-gray-600 text-sm mb-1">Alternate Arrangement:</p>
                          <p className="bg-green-50 p-3 rounded text-sm border border-green-200">
                            {affectedClass.alternate_arrangement || 'No arrangement specified'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!selectedLeave.affected_classes || selectedLeave.affected_classes.length === 0) && (
                <div className="bg-green-50 border-2 border-green-300 p-4 rounded-lg text-center">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-green-900 font-semibold">No classes affected during this leave period</p>
                </div>
              )}

              {/* Admin Comment */}
              {selectedLeave.admin_comment && (
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-900 mb-2">Admin Comment</h3>
                  <p className="text-gray-800 bg-white p-3 rounded border border-purple-200">{selectedLeave.admin_comment}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setShowLeaveDetailsModal(false)}>
                  Close
                </Button>
                {selectedLeave.status === 'pending' && (isAdmin || isSuperAdmin) && (
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                      onClick={() => {
                        handleApproveLeave(selectedLeave.id);
                        setShowLeaveDetailsModal(false);
                      }}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve Leave
                    </Button>
                    <Button
                      variant="outline"
                      className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                      onClick={() => {
                        handleRejectLeave(selectedLeave.id);
                        setShowLeaveDetailsModal(false);
                      }}
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Reject Leave
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PayrollManagement;